import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import {
  Type,
  setCookies,
  useAnalyticsClient,
  useEnv,
  write,
} from '@acorns/web-utils'

import {
  SignInWithAppleButtonTappedCta_title,
  SignInWithAppleButtonViewedCta_title,
  trackSignInWithAppleButtonTappedPayload,
  trackSignInWithAppleButtonViewedPayload,
} from 'generated/trackingEvents'
import {
  ValidateCredentialsMutation,
  useValidateCredentialsMutation,
} from 'generated/types'
import { routes } from 'src/routes'
import useAnalyticsComponentViewed from 'utils/analytics/useAnalyticsComponentViewed'
import { extractOAuthParams } from 'utils/oAuth/extractOAuthParams'
import { twoMinutesFromNow } from 'utils/oAuth/twoMinutesFromNow'
import { getUdidForAuthentication } from 'utils/udid-utils'

import { ANALYTICS_TRACKING_SCREEN } from '../utils/constants'
import {
  generateHashedNonce,
  generateNonce,
} from '../utils/generateHashedNonce'
import { AppleIconSvg } from './AppleIconSvg'
import {
  ERROR_TYPES,
  ErrorType,
  SignInWithAppleErrorModal,
} from './SignInWithAppleErrorModal'
import {
  AppleButton,
  AppleButtonContainer,
  CustomTextWrapper,
} from './styled-components'

type SignInWithAppleProps = {
  isOAuth?: boolean
}

export const SignInWithApple = ({ isOAuth = false }: SignInWithAppleProps) => {
  const { env } = useEnv()
  const [errorType, setErrorType] = useState<ErrorType | null>(null)
  const [suspendedToken, setSuspendedToken] = useState('')
  const { track } = useAnalyticsClient()
  const appleSignInSuccessListenerRef = useRef<(event: any) => void>(null)

  const containerRef = useAnalyticsComponentViewed({
    generatedPayload: trackSignInWithAppleButtonViewedPayload({
      screen: ANALYTICS_TRACKING_SCREEN,
      ctaTitle: SignInWithAppleButtonViewedCta_title.ContinueWithApple,
    }),
  })

  const history = useHistory()
  const location = useLocation()

  const [validateCredentials] = useValidateCredentialsMutation({
    onCompleted: (data: ValidateCredentialsMutation) => {
      if (
        data.validateCredentials.__typename === 'AuthSession' &&
        data.validateCredentials
      ) {
        if (isOAuth) {
          write(Type.identityId, data.validateCredentials.identityId, {
            expires: twoMinutesFromNow(),
          })

          const {
            chromeExtensionPartnerFlow,
            oAuthPartnerParams,
            preservedRedirect,
            preservedQueryParams,
          } = extractOAuthParams(location)

          // @ts-expect-error: 'data' is not a standard property, but is established in this repo
          history.push({
            pathname: routes.oAuth.authorize,
            data: {
              preservedRedirect,
              preservedQueryParams,
              chromeExtensionPartnerFlow,
              oAuthPartnerParams,
              isMfaExemptOAuthUser: true,
            },
          })

          return
        }

        if (
          data.validateCredentials.token &&
          data.validateCredentials.refreshToken
        ) {
          setCookies({
            token: data.validateCredentials.token,
            refreshToken: data.validateCredentials.refreshToken,
            identityId: data.validateCredentials.identityId,
          })

          window.location.href = env.get('defaultRedirect')
        }
      }

      initializeAppleAuth()

      if (
        data.validateCredentials.__typename ===
        'OidcCredentialsNotFoundException'
      ) {
        setErrorType(ERROR_TYPES.ACCOUNT_NOT_LINKED)
        return
      }

      if (data.validateCredentials.__typename === 'UserSuspendedException') {
        setSuspendedToken(data.validateCredentials.suspendedToken)
        setErrorType(ERROR_TYPES.ACCOUNT_SUSPENDED)
        return
      }

      if (data.validateCredentials.__typename === 'DeviceBlockedException') {
        setErrorType(ERROR_TYPES.DEVICE_BLOCKED)
        return
      }

      if (data.validateCredentials.__typename === 'LoginRateLimitedException') {
        setErrorType(ERROR_TYPES.LOGIN_RATE_LIMITED)
        return
      }

      setErrorType(ERROR_TYPES.DEFAULT_ERROR)
    },
  })

  const initializeAppleAuth = async () => {
    const nonce = await generateHashedNonce()
    const crossSiteReturnState = generateNonce()

    window.AppleID.auth.init({
      clientId: env.get('signInWithAppleClientId'),
      scope: 'name email',
      redirectURI: env.get('signInWithAppleRedirectUrl'),
      state: crossSiteReturnState,
      nonce,
      usePopup: true,
    })

    if (appleSignInSuccessListenerRef.current) {
      window.document.removeEventListener(
        'AppleIDSignInOnSuccess',
        appleSignInSuccessListenerRef.current,
      )
    }

    const eventListener = (event: any) => {
      const externalIdToken = event.detail.authorization.id_token
      const returnedState = event.detail.authorization.state

      if (returnedState === crossSiteReturnState) {
        validateCredentials({
          variables: {
            udid: getUdidForAuthentication(),
            idToken: externalIdToken,
            nonce,
            oauth: isOAuth,
          },
        })
        return
      }

      setErrorType(ERROR_TYPES.DEFAULT_ERROR)
    }

    appleSignInSuccessListenerRef.current = eventListener

    window.document.addEventListener('AppleIDSignInOnSuccess', eventListener)
  }

  useEffect(function loadSdk() {
    const script = window.document.createElement('script')

    script.src =
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

    script.onload = async function initializeSdk() {
      initializeAppleAuth()
    }

    window.document.body.appendChild(script)
  }, [])

  const handleAppleButtonClick = () => {
    track(
      ...trackSignInWithAppleButtonTappedPayload({
        screen: ANALYTICS_TRACKING_SCREEN,
        ctaTitle: SignInWithAppleButtonTappedCta_title.ContinueWithApple,
      }),
    )
  }

  const handleCloseModal = () => {
    setErrorType(null)
  }

  return (
    <>
      <AppleButtonContainer onClick={handleAppleButtonClick}>
        <AppleButton
          id="appleid-signin"
          data-color="black"
          data-border="true"
          data-type="sign in"
          data-border-radius="50"
          style={{ width: '100%', height: '44px' }}
          ref={containerRef}
        />
        <CustomTextWrapper>
          <AppleIconSvg />
          <span>Continue with Apple</span>
        </CustomTextWrapper>
      </AppleButtonContainer>
      <SignInWithAppleErrorModal
        onClose={handleCloseModal}
        errorType={errorType}
        suspendedToken={suspendedToken}
      />
    </>
  )
}
